import { lazy } from 'react'

// A route chunk that fails to download takes the whole app down: React.lazy
// caches the rejected thenable, so re-entering the route keeps throwing until
// the document is reloaded. Prod hit this on 2026-08-09 during a ~1 minute
// network disruption — one dropped request for the order-editor chunk replaced
// the entire UI with the ErrorBoundary screen.
const MAX_ATTEMPTS = 3
const BACKOFF_MS = [300, 900]
const RELOAD_STAMP_KEY = 'chunkReloadedAt'
const RELOAD_GUARD_MS = 60_000

/**
 * True when an error came from the browser failing to download a route chunk
 * (Vite emits native `import()` for lazy routes) rather than from the module's
 * own code.
 *
 * Browsers word the rejection differently:
 *   Chromium  `Failed to fetch dynamically imported module: <url>`
 *   Firefox   `error loading dynamically imported module: <url>`
 *   Safari    `Importing a module script failed.`
 * A stale build behind the SPA 404 fallback lands here too: the missing asset
 * comes back as index.html and the import fails on the MIME type. Vite's
 * preload helper reports a chunk's CSS the same way, with
 * `Unable to preload CSS for <url>`.
 */
const CHUNK_LOAD_MESSAGES = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
]

export function isChunkLoadError(error) {
  if (!error) return false
  const message = error.message || ''
  return CHUNK_LOAD_MESSAGES.some((pattern) => pattern.test(message))
}

/**
 * Claims this tab's single automatic reload. Returns false when one was already
 * taken recently, which is what stops a permanently missing chunk from becoming
 * a reload loop.
 */
function claimReload() {
  try {
    const stamp = Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY))
    if (stamp && Date.now() - stamp < RELOAD_GUARD_MS) return false
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()))
    return true
  } catch {
    // Storage blocked (Safari private mode, hardened settings). Without a guard
    // we cannot tell a first failure from a loop, so never reload.
    return false
  }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

async function importWithRetry(factory) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await factory()
    } catch (error) {
      // A module that throws while evaluating is a code fault — retrying it
      // just repeats the throw, and reloading hides it.
      if (!isChunkLoadError(error)) throw error

      if (attempt < MAX_ATTEMPTS - 1) {
        await wait(BACKOFF_MS[attempt])
        continue
      }

      if (claimReload()) {
        window.location.reload()
        // Never settle: Suspense keeps its fallback up while the document
        // unloads instead of flashing the error screen on the way out.
        return new Promise(() => {})
      }

      throw error
    }
  }
}

/**
 * Drop-in replacement for React.lazy that retries the dynamic import before
 * giving up, then takes one guarded page reload. The reload also covers the
 * stale-build case, where the running document references filenames a newer
 * deploy has replaced — only a fresh index.html can point at the new ones.
 */
export function lazyWithRetry(factory) {
  return lazy(() => importWithRetry(factory))
}
